import React, { useEffect, useState } from 'react';
import { useLoading } from '../Loading';
import { collection, getDocs, onSnapshot, query, Timestamp, where } from 'firebase/firestore';
import db from '../../firebase/firebaseConfig';
import { useAuth } from '../../contexts/authContext';
import { Tooltip, Legend, LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer  } from 'recharts';
import { useNavigate } from 'react-router-dom';



function ShelterDashboard() {
    const { setLoading } = useLoading();
    const {currentUser} = useAuth();
    const [applicantData, setApplicantData] = useState([]);
    const [count, setCount] = useState({
        pendingApplicants:0,
        todayAppointments:0,
        availablePets:0,
        adoptedPets:0,
        upcomingAppointments:0,
        pendingFinal:0,
    });
    const navigate = useNavigate();


    useEffect(()=>{

        if (!currentUser) {
            navigate('/login');
            return;
        }

        const today = new Date();
        const now = new Date();

        const padToTwoDigits = (num) => String(num).padStart(2, '0');
        const todayDate = padToTwoDigits(today.getMonth() + 1) + "/" + padToTwoDigits(today.getDate()) + "/" + today.getFullYear();
        const currentTimestamp = Timestamp.fromDate(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), now.getUTCHours(), now.getUTCMinutes(), now.getUTCSeconds(), now.getUTCMilliseconds())));

        const setupListeners = () => {
            const listeners = [
                {
                    ref: query(collection(db, "Applications"), where("shelterId", "==", currentUser.uid), where("status", "==", "Pending")),
                    stateKey: "pendingApplicants",
                },
                {
                    ref: query(collection(db, "Appointments"), where("shelterId", "==", currentUser.uid), where("date", "==", todayDate), where("status", "==", "Pending")),
                    stateKey: "todayAppointments",
                },
                {
                    ref: query(collection(db, "Pets"), where("shelterId", "==", currentUser.uid), where("status", "!=", "Adopted")),
                    stateKey: "availablePets",
                },
                {
                    ref: query(collection(db, "Pets"), where("shelterId", "==", currentUser.uid), where("status", "==", "Adopted")),
                    stateKey: "adoptedPets",
                },
                {
                    ref: query(collection(db, "Appointments"), where("shelterId", "==", currentUser.uid), where("appointmentTime", ">", currentTimestamp), where("status", "==", "Pending")),
                    stateKey: "upcomingAppointments",
                },
                {
                    ref: query(collection(db, "Appointments"), where("shelterId", "==", currentUser.uid), where("status", "==", "Pending"), where("appointmentTime", "<", currentTimestamp)),
                    stateKey: "pendingFinal",
                },
            ];

            const unsubscribeFunctions = listeners.map(({ ref, stateKey }) =>
                onSnapshot(ref, (snapshot) => {
                    setCount((prevCount) => ({
                        ...prevCount,
                        [stateKey]: snapshot.size,
                    }));
                }, (error) => {
                    console.error(`Error getting ${stateKey}:`, error);
                    setLoading(false);
                })
            );

            const fetchMonthlyData = async () => {
                const applicantQuery = query(collection(db, 'Applications'), where('shelterId', '==', currentUser.uid));
                const applicantSnapshot = await getDocs(applicantQuery);

                const monthlyCounts = {};

                applicantSnapshot.forEach((doc) => {
                    const timestamp = doc.data().timestamp;
                    if (timestamp && timestamp.toDate) {
                        const applicationDate = timestamp.toDate();
                        const monthYear = applicationDate.toLocaleString('default', { month: 'short', year: 'numeric' });
                        monthlyCounts[monthYear] = (monthlyCounts[monthYear] || 0) + 1;
                    } else {
                        console.error("Invalid timestamp in document:", doc.id);
                    }
                });

                const monthlyData = Object.keys(monthlyCounts).map((monthYear) => ({
                    month: monthYear,
                    applications: monthlyCounts[monthYear],
                }));

                setApplicantData(monthlyData.reverse());
                setLoading(false);
            };

            fetchMonthlyData();

            return () => {
                unsubscribeFunctions.forEach((unsubscribe) => unsubscribe());
            };
        };

        return setupListeners();

       
    },[currentUser]);


    return (
        <div className="dashboard">
            <h1>Dashboard</h1>
            <div className='highlight'>
                <div className="dataBox" style={{ backgroundColor: "#cd78ff" }}>
                    <h2>{count.pendingApplicants}</h2>
                    <p>Total Pending Applicants</p>
                </div>

                <div className="dataBox" style={{ backgroundColor: "#70d2ff" }}>
                    <h2>{count.todayAppointments}</h2>
                    <p>Today Appointment</p>
                </div>
            </div>

            <div className="stats-container">
                <div className="dataBox" style={{backgroundColor:"#b0df9e"}}>
                    <h2>{count.availablePets}</h2>
                    <p>Total Available Pet</p>
                </div>

                <div className="dataBox" style={{backgroundColor:"#8ca9f4"}}>
                    <h2>{count.adoptedPets}</h2>
                    <p>Total Completed Pet</p>
                </div>

                <div className="dataBox" style={{backgroundColor:"#fc96c4"}}>
                    <h2>{count.upcomingAppointments}</h2>
                    <p>Upcoming Appointment</p>
                </div>

                <div className="dataBox" style={{backgroundColor:"#f8aa6b"}}>
                    <h2>{count.pendingFinal}</h2>
                    <p>Pending Final Approval</p>
                </div>

            </div>
            <div className="chart-container" >
                <h2>Applications Over Months</h2>
                <ResponsiveContainer width="100%" height={300}> {/* Use ResponsiveContainer */}
                    <LineChart data={applicantData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="applications" stroke="#8884d8" />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}


export default ShelterDashboard;
